import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { bookingStatus as bookingStatusType } from 'types/booking';
import { paymentsShape } from 'constants/propTypes';

import { Row, Column } from 'components/shared/Layout';
import { LinkButton } from 'components/ui/Button';
import ManageHotel from './ManageHotel';
import ExtrasHotelDetails from './ExtrasHotelDetails';

import Hotel from './Hotel';

import { StyledExtrasContainer, HotelDetailsHintArrow, ApiUsedMessage } from '../Extras.styles';
import { hasItem } from '../Extras.utils';
import { HOTEL_BOOKING } from 'store/queries';
import { useQuery } from '@apollo/client';
import { getHotelsInfo, getHotelsStatuses } from 'utils/booking';
import { buildHotelInfoFromDotwBooking } from 'utils/hotel';

const ExtrasHotel = ({ hotels, additionalDetails, bookingReference, bookingStatus, currency, isPackage, isHotelOnly, payments = [] }) => {
  const [isHotelDetailsExpanded, setHotelDetailsExpanded] = useState(false);
  const [canExchange, toggleCanExchange] = useState(true);

  const isCompensated = additionalDetails.find((ad) => ad.key === 'HOTEL_COMPENSATED')?.value === 'true';

  const isDotwBooking = hotels?.[0]?.isDotwBooking;

  const { data } = useQuery(HOTEL_BOOKING, {
    variables: { reference: bookingReference },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    const statuses = data?.hotelBooking && getHotelsStatuses(hotels, data.hotelBooking, isDotwBooking);
    const info = data?.hotelBooking && getHotelsInfo(hotels, [buildHotelInfoFromDotwBooking(data.hotelBooking)], isDotwBooking);
    // if at least one hotel status in jac travel matches to one hotel status in s3 we assume all hotels statuses match
    // this is because we can not tell what s3 hotel a particular jac travel hotel data corresponds to
    const hasHotelWithStatusConflict = statuses?.length && !statuses.find((h) => h.s3Status === h.jacTravelStatus);
    const hasHotelWithInfoConflict =
      info?.length &&
      info.every(
        (h) =>
          (h.s3CheckInDate && h.jacTravelCheckInDate && h.s3CheckInDate !== h.jacTravelCheckInDate) ||
          (h.s3GuestCount && h.jacTravelGuestCount && h.s3GuestCount !== h.jacTravelGuestCount) ||
          (h.s3HotelPrice && h.jacTravelHotelPrice && h.s3HotelPrice !== h.jacTravelHotelPrice) ||
          (h.s3NumberOfNights && h.jacTravelNumberOfNights && h.s3NumberOfNights !== h.jacTravelNumberOfNights)
      );

    if (hasHotelWithStatusConflict || hasHotelWithInfoConflict || data?.hotelBooking == null) {
      toggleCanExchange(true);
    } else {
      toggleCanExchange(false);
    }
  }, [hotels, data, toggleCanExchange]);

  return (
    <Row span={14} start={2}>
      <h1>
        <FormattedMessage id="extras.hotel.title" />
      </h1>

      {hasItem(hotels) ? (
        hotels.map(
          (hotel) =>
            !hotel.cancelled && (
              <>
                <span>
                  <LinkButton
                    onClick={() => setHotelDetailsExpanded(!isHotelDetailsExpanded)}
                    textDecoration="none"
                    margin="0 0 8px 0"
                    padding="0"
                  >
                    <FormattedMessage id="extras.hotel.hotelDetailsHint" />
                    <HotelDetailsHintArrow isExpanded={isHotelDetailsExpanded}>{'>'}</HotelDetailsHintArrow>
                  </LinkButton>
                </span>
                <ApiUsedMessage>
                  {hotel.isDotwBooking ? (
                    <>
                      <FormattedMessage id={'extras.hotel.dotwApi'} /> ({isPackage ? 2030145 : 2030165})
                    </>
                  ) : (
                    <>
                      <FormattedMessage id={'extras.hotel.jacApi'} />
                    </>
                  )}
                </ApiUsedMessage>

                {isHotelDetailsExpanded && (
                  <ExtrasHotelDetails
                    hotelExternalReference={hotel.jacTravelRef}
                    isPackage={isPackage}
                    isHotelOnly={isHotelOnly}
                    isDotwBooking={hotel.isDotwBooking}
                    reference={bookingReference}
                  />
                )}
                <StyledExtrasContainer>
                  <Hotel hotel={hotel} additionalDetails={additionalDetails}>
                    <ManageHotel
                      canBeCancelledOnThirdParty={hotel.canBeCancelledOnThirdParty}
                      isConfirmed={bookingStatus === bookingStatusType.CONFIRMED}
                      bookingReference={bookingReference}
                      currency={hotel.currency}
                      date={hotel.checkInDate}
                      validityEndDate={hotel.validityEndDate}
                      payments={payments}
                      hotelId={hotel.hotelId}
                      itemRef={hotel.itemRef}
                      priceWithMargin={hotel.priceWithMargin}
                      isPackage={isPackage}
                      isHotelOnly={isHotelOnly}
                      margin={hotel.margin}
                      isCompensated={isCompensated}
                      isDotwBooking={hotel.isDotwBooking}
                      canExchange={canExchange}
                    />
                  </Hotel>
                </StyledExtrasContainer>
              </>
            )
        )
      ) : (
        <Column flexDirection="column">
          <div>
            <FormattedMessage id="extras.noHotelProductsBookingLevel" />
          </div>
        </Column>
      )}
    </Row>
  );
};

ExtrasHotel.propTypes = {
  bookingReference: PropTypes.string.isRequired,
  bookingStatus: PropTypes.string.isRequired,
  hotels: PropTypes.arrayOf(
    PropTypes.shape({
      cancelled: PropTypes.bool,
      passenger: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string
      }),
      checkInDate: PropTypes.string,
      jacTravelRef: PropTypes.string,
      hotelId: PropTypes.string,
      eurostarReference: PropTypes.string,
      currency: PropTypes.string,
      price: PropTypes.number,
      itemRef: PropTypes.string.isRequired,
      isDotwBooking: PropTypes.bool
    })
  ),
  currency: PropTypes.string.isRequired,
  isPackage: PropTypes.bool.isRequired,
  isHotelOnly: PropTypes.bool,
  additionalDetails: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      key: PropTypes.string,
      value: PropTypes.string
    })
  ),
  payments: paymentsShape
};

export default ExtrasHotel;
